import TermsAndPoliciesFooter from '@/operator/components/general/termsAndPolicies/TermsAndPoliciesFooter';
import { grayText } from '@/operator/layout/theme/theme';
import { Container } from '@mui/material';
import Typography from '@mui/material/Typography';
import type { TypographyProps } from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
    StyledMessageWithTipContainer,
    StyledWidthWrapper,
} from '@/operator/components/modules/AuthPage/setup/Styles';
import React from 'react';

type descriptionType = string | React.ReactElement | null;

export const Links = {
    waitList: 'https://share.hsforms.com/1AARbSslvRvSMc6WE5dFaNgcwasu',
};

interface MessageWithTipProps {
    headline: string;
    headlineVariant?: TypographyProps['variant'];
    description: string | React.ReactElement | null;
    secondDescription?: descriptionType;
    thirdDescription?: descriptionType;
    tip?: string | null;
    // biome-ignore lint/suspicious/noExplicitAny: inherited from operator
    Icon: React.FC<any>;
    iconDescription?: string | null;
    termsFooter?: boolean;
    footerSlot?: JSX.Element;
    tipHeadline?: string | null;
    waitList?: string | null;
    signInButton?: React.ReactElement;
}

const IconContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
`;

export const MessageWithTip = ({
    headline,
    headlineVariant = 'h1',
    description,
    secondDescription = null,
    thirdDescription = null,
    tip,
    iconDescription,
    termsFooter = true,
    footerSlot,
    tipHeadline,
    waitList = null,
    signInButton,
    Icon,
}: MessageWithTipProps): JSX.Element => {
    const { t } = useTranslation();

    const renderDescription = () => {
        if (!description) {
            return null;
        }

        if (typeof description === 'string') {
            return (
                <>
                    <Typography variant="body">{description}</Typography>

                    {secondDescription && (
                        <>
                            <Typography variant="body">
                                {secondDescription}
                            </Typography>
                            <Typography variant="body">
                                {thirdDescription}
                            </Typography>
                        </>
                    )}
                </>
            );
        }

        return description;
    };

    const marginBot = (tip || footerSlot) && !signInButton ? '47px' : '0px';

    return (
        <StyledMessageWithTipContainer>
            <StyledWidthWrapper
                style={{
                    marginBottom: marginBot,
                }}
            >
                <IconContainer>
                    <Icon />
                    {iconDescription && (
                        <Typography
                            variant="body2"
                            style={{ color: '#5B5B5B' }}
                            margin="0px 10px 25px"
                        >
                            {iconDescription}
                        </Typography>
                    )}
                </IconContainer>
                <Typography variant={headlineVariant} marginBottom={'1.3rem'}>
                    {headline}
                </Typography>
                {renderDescription()}
            </StyledWidthWrapper>
            {tip && (
                <StyledWidthWrapper>
                    <Container
                        sx={{
                            padding: '0px !important',
                            flexDirection: 'column',
                        }}
                    >
                        <Typography variant="h5" margin={0}>
                            {tipHeadline ?? t('General.Tip.Headline')}
                        </Typography>

                        <Typography
                            variant="bodySmall"
                            style={{
                                color: grayText,
                                lineHeight: '1.25rem',
                            }}
                        >
                            {tip}
                            {waitList && (
                                <a
                                    style={{ color: '#4C17AD' }}
                                    href={Links.waitList}
                                >
                                    {waitList}
                                </a>
                            )}
                        </Typography>
                    </Container>
                </StyledWidthWrapper>
            )}
            {signInButton}
            {footerSlot}
            {termsFooter && <TermsAndPoliciesFooter />}
        </StyledMessageWithTipContainer>
    );
};

export default MessageWithTip;
